import { motion } from "framer-motion"
import {
  RiFileList3Fill,
  RiShieldCheckFill,
  RiUserSettingsFill,
} from "@remixicon/react"
import { Link } from "react-router-dom"

import { ColorBand, ColorBandInner } from "@/components/landing/ColorBand"
import { MarketingPageShell } from "@/components/landing/MarketingPageShell"
import {
  fadeUp,
  inView,
  staggerContainer,
  staggerItem,
} from "@/components/landing/marketing-motion"

const blocks = [
  {
    title: "Legal documents",
    body: "Terms, privacy, and merchant agreements describe how Sqaleshop is offered and how data is handled. Read them in full before going live.",
    Icon: RiFileList3Fill,
    tone: "bg-[#d4c4fc]",
    links: [
      { to: "/terms", label: "Terms & Conditions" },
      { to: "/privacy", label: "Privacy policy" },
      { to: "/merchant-agreement", label: "Merchant agreement" },
    ],
  },
  {
    title: "Merchant responsibilities",
    body: "You are responsible for accurate listings, lawful products, taxes where applicable, and how you treat customer data in your own marketing.",
    Icon: RiUserSettingsFill,
    tone: "bg-[#efe04a]",
    links: [] as { to: string; label: string }[],
  },
  {
    title: "Security posture",
    body: "We build with modern practices and expect merchants to protect account access (strong passwords, limited staff roles). Report security concerns to hello@sqaleshop.com.",
    Icon: RiShieldCheckFill,
    tone: "bg-[#d2e4c4]",
    links: [] as { to: string; label: string }[],
  },
] as const

export function CompliancePage() {
  return (
    <MarketingPageShell
      eyebrow="Compliance"
      title="Trust, clarity, and responsibility"
      description="Sqaleshop is built for small businesses. This page summarizes how we think about compliance and where to find formal policies, it is not legal advice."
      heroVariant="muted"
      bare
    >
      <ColorBand className="bg-white text-primary">
        <ColorBandInner>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={inView}
            className="grid gap-5 lg:grid-cols-3"
          >
            {blocks.map((b) => (
              <motion.article
                key={b.title}
                variants={staggerItem}
                className={`flex flex-col rounded-[2.5rem] p-7 sm:p-8 ${b.tone}`}
              >
                <span className="flex size-12 items-center justify-center rounded-2xl bg-white/70 text-primary">
                  <b.Icon className="size-6" />
                </span>
                <h2 className="font-heading mt-5 text-xl text-primary">
                  {b.title}
                </h2>
                <p className="mt-3 flex-1 text-sm font-semibold leading-relaxed text-primary/65">
                  {b.body}
                </p>
                {b.links.length > 0 ? (
                  <ul className="mt-5 flex flex-col gap-2">
                    {b.links.map((l) => (
                      <li key={l.to}>
                        <Link
                          to={l.to}
                          className="text-major text-sm text-primary underline-offset-4 hover:underline"
                        >
                          {l.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </motion.article>
            ))}
          </motion.div>

          <motion.p
            {...fadeUp}
            className="mt-12 text-center text-sm font-semibold text-primary/55"
          >
            Questions?{" "}
            <Link to="/contact" className="text-primary hover:underline">
              Contact us
            </Link>
            .
          </motion.p>
        </ColorBandInner>
      </ColorBand>
    </MarketingPageShell>
  )
}
